import type { TeamAccessStatus, TeamFunction, TeamMemberDto } from '@/lib/api/v2/team'

/**
 * SOLY-TEAM-001 vocabulary (design decision): a "Fonction" is what the person
 * does on the field (feeds the planning), the "Rôle d'accès" is what they may
 * do in Soly. Never display a bare "Rôle" (collides with the Commercial
 * function) and never display "Compétence".
 */
export const FUNCTION_LABELS: Record<TeamFunction, string> = {
  Commercial: 'Commercial',
  Surveyor: 'Métreur',
  Installer: 'Placeur',
}

export const ALL_FUNCTIONS: TeamFunction[] = ['Commercial', 'Surveyor', 'Installer']

export function functionLabel(fn: TeamFunction): string {
  return FUNCTION_LABELS[fn] ?? fn
}

export type AccessBadge = {
  label: string
  variant: 'default' | 'secondary' | 'outline'
}

/** Access column badge — the role label rides along for active members. */
export function getAccessBadge(status: TeamAccessStatus): AccessBadge {
  switch (status) {
    case 'active':
      return { label: 'Actif', variant: 'default' }
    case 'invited':
      return { label: 'Invitation envoyée', variant: 'secondary' }
    case 'deactivated':
      return { label: 'Désactivé', variant: 'outline' }
    case 'none':
    default:
      return { label: "Pas d'accès", variant: 'outline' }
  }
}

/** Multi-field search: name, email, function label, access role label. */
export function filterMembers(members: TeamMemberDto[], search: string): TeamMemberDto[] {
  const q = search.trim().toLowerCase()
  if (!q) return members
  return members.filter((m) => {
    return (
      m.displayName.toLowerCase().includes(q) ||
      (m.email ?? '').toLowerCase().includes(q) ||
      m.functions.some((f) => functionLabel(f).toLowerCase().includes(q)) ||
      (m.role?.label ?? '').toLowerCase().includes(q)
    )
  })
}

/** Active rows feed the main table; deactivated ones the "Inactifs" section. */
export function splitByActivity(members: TeamMemberDto[]): {
  active: TeamMemberDto[]
  inactive: TeamMemberDto[]
} {
  return {
    active: members.filter((m) => m.isActive),
    inactive: members.filter((m) => !m.isActive),
  }
}

/**
 * The Fonctions column (and the add-dialog checkboxes) only make sense with
 * the planning enabled — the page itself stays fully functional flag off.
 */
export function showFunctionsColumn(planningEnabled: boolean | undefined): boolean {
  return planningEnabled === true
}

/** "Désactivé le JJ/MM/AAAA" for the Inactifs rows. */
export function formatDeactivatedAt(iso: string | null): string {
  if (!iso) return 'Désactivé'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Désactivé'
  const formatted = date.toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  return `Désactivé le ${formatted}`
}

/**
 * SOLY-PLAN-005 — which crew each person is in, derived from the crew list.
 *
 * The crew rides on the crews endpoint rather than on the team roster: this screen needs the full
 * list anyway (to offer it), and a second source for the same fact is a second thing to keep in
 * sync. One entry per person, because the DB allows exactly one (V135 unique index) — the select
 * below is a single choice for the same reason.
 */
export function crewIdByUser(crews: { id: string; memberUserIds: string[] }[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const crew of crews) {
    for (const userId of crew.memberUserIds) {
      map[userId] = crew.id
    }
  }
  return map
}

/** Crews in display order: by name, fr-BE, so the select reads like the agenda. */
export function sortCrews<T extends { name: string }>(crews: readonly T[]): T[] {
  return [...crews].sort((a, b) => a.name.localeCompare(b.name, 'fr-BE'))
}

/**
 * Whether the crew column is offered. Same condition as the Fonctions column — a crew groups agenda
 * lanes, so with the planning off it configures something the org cannot see.
 */
export function showCrewColumn(planningEnabled: boolean | undefined): boolean {
  return showFunctionsColumn(planningEnabled)
}
